/**
 * The three signals `BillingPage` needs from its host, derived ONCE.
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 *
 * `App.tsx` and `MobileShell.tsx` each computed these inline, and they
 * disagreed: desktop gated on `activeRole === 'owner' || 'admin'`, mobile on
 * `canManageBilling`. So a person explicitly granted `manage_billing:write`
 * without an admin role got the billing controls on both shells and a
 * different destination on each (finding D2). Two shells, one question, one
 * answer — computed here.
 *
 * ── The split that matters, and the bug that produced it ─────────────────
 *
 * `onOpenBudgets` used to carry TWO unrelated jobs, and `BillingPage` gated
 * both on the same prop:
 *
 *   1. the **View detailed usage** hand-off button, and
 *   2. the *"Members' own allowances run on the calendar month…"* sentence.
 *
 * Only (2) is wrong on a personal drive — there are no members. (1) is
 * perfectly correct there, because a personal drive **does** have a Spending
 * page (`OrgBudgetDashboard` in `personal` mode). Both shells nonetheless
 * passed the prop unconditionally, so a solo user was told about "members"
 * they do not have (finding D1) — and `BillingPage`'s own docblock still
 * claimed the hosts passed `!isPersonal && <access>`, which had stopped being
 * true.
 *
 * The obvious fix — gate the prop on `!isPersonal` — would have silenced the
 * sentence by **deleting a working button**. So the sentence gets its own
 * signal (`showMembersClockNote`) and the hand-off stays wired for everyone.
 * That is why this returns three fields rather than two.
 */

export interface BillingHandoffInput {
  /** Personal drive: no members, no members page, no seats to add. */
  isPersonal: boolean
  /** `manage_budget:read` — may this caller see the org's spend dashboard?
   *  On a personal drive `useMyCapabilities` resolves every capability to
   *  true (implicit owner), so this is already true there. */
  canReadBudget: boolean
  /** `manage_billing:write` — may this caller change the commercial
   *  relationship? Gates seats, which is a member-management action. */
  canManageBilling: boolean
  /** Route prefix for the active tenant (`/~` or `/t/<id>`). */
  prefix: string
  navigate: (to: string) => void
}

export interface BillingHandoff {
  /**
   * Always defined. The question is only WHERE it lands:
   *
   *   may read the org's spend  →  Settings → Spending (everyone's spend)
   *   may not                   →  Settings → Usage    (their own)
   *
   * A personal drive takes the first branch: `canReadBudget` is true there and
   * `SpendingPage` renders the drive's own dashboard, which is exactly the
   * "detailed usage" the button promises.
   */
  onOpenBudgets: () => void
  /**
   * Settings → Storage, for the key-info card's storage tile. Always defined:
   * every tenant (personal included) has a storage page, and the read behind it
   * is membership-gated only.
   */
  onOpenStorage: () => void
  /**
   * Settings → Members, for the key-info card's included-seats tile. Undefined
   * on a personal drive (no members page), which leaves the tile a static
   * figure. Unlike `onManageSeats` this is a plain navigation, NOT gated on
   * `manage_billing:write` — viewing the members page has its own gates, and
   * the tile is a read-through link, not a seat-management action.
   */
  onOpenMembers?: () => void
  /** Undefined on a personal drive (no members page) or without
   *  `manage_billing:write`, which hides "Add seats" rather than offering an
   *  action the server would refuse. */
  onManageSeats?: () => void
  /**
   * Whether to print the note explaining that MEMBERS' allowances run on the
   * calendar month while this page runs on the billing period.
   *
   * False on a personal drive — the sentence is about members, and there are
   * none. This is the whole of D1: it is a statement about the tenant's shape,
   * not about the reader's permissions, so it cannot ride on a permission prop.
   */
  showMembersClockNote: boolean
}

export function billingHandoff(input: BillingHandoffInput): BillingHandoff {
  const { isPersonal, canReadBudget, canManageBilling, prefix, navigate } = input
  return {
    onOpenBudgets: canReadBudget
      ? () => navigate(`${prefix}/settings/budget`)
      : () => navigate(`${prefix}/settings/usage`),
    onOpenStorage: () => navigate(`${prefix}/settings/storage`),
    onOpenMembers: !isPersonal ? () => navigate(`${prefix}/settings/members`) : undefined,
    onManageSeats: !isPersonal && canManageBilling
      ? () => navigate(`${prefix}/settings/members`)
      : undefined,
    showMembersClockNote: !isPersonal,
  }
}
